import { startSignin } from '../signin-analytics.js';
import { signinSource } from '../../shared/signin-analytics.js';
import { EmailSigninSession } from '../email-signin-session.js';
import { datasetSaveOffer, type DatasetSaveOffer } from './dataset-save-offer.js';
import { mountGoogleSignIn } from '../google-signin.js';
import { magicLinkFailureReason } from '../../shared/magic-link-errors.js';
import { signupAnalyticsFields, signupAnalyticsUrl } from '../customer-analytics-signup.js';
import { isFunction, isSchemaRecord, isString } from '../../shared/runtime-values.js';
import type { SchemaInput } from '../../shared/schema-input.js';
import { LitElement, type TemplateResult } from 'lit';
import { html } from 'lit/html.js';
import type { SigninActions } from '../store/panel-action-contracts.js';
import { t } from '../i18n.js';
import { getAppOverlayHost } from '../app-overlay-host.js';
import { requireActions } from './required-actions.js';
import { normalizeEmailCodeInput } from '../email-code-input.js';

function signedInEmail(value: SchemaInput): string | null {
  if (!isSchemaRecord(value) || !isSchemaRecord(value.user)) return null;
  return isString(value.user.email) ? value.user.email : null;
}

type GoogleSigninAttempt =
  | { status: 'closed' }
  | { status: 'ready' | 'refreshing' | 'redirecting'; token: symbol }
  | { status: 'popup'; token: symbol; popup: Window; removeMessage: () => void };

export class SigninDialogElement extends LitElement {
  static init(actions: SigninActions) {
    const el = new SigninDialogElement();
    el.actions = actions;
    getAppOverlayHost().appendChild(el);
    return el;
  }

  static properties: any = {
    statusMessage: { type: String },
    _datasetSave: { state: true },
    _resendSeconds: { state: true },
    _titleOverride: { type: String, state: true },
    _descriptionOverride: { type: String, state: true },
  };

  declare private _datasetSave: DatasetSaveOffer | null;
  private readonly _emailSession = new EmailSigninSession(() => this.requestUpdate());
  private get _sentEmail() { return this._emailSession.delivery?.email ?? ''; }
  private get _challenge() { return this._emailSession.delivery?.challenge ?? ''; }
  private get _emailExpiresAt() { return this._emailSession.delivery?.expiresAt ?? 0; }
  private get _sending() { return this._emailSession.sending; }
  private get _verifying() { return this._emailSession.verifying; }
  private _saveAfterSignin = false;
  private _onCodeSuccessCallback: (() => void) | null = null;
  declare private _resendSeconds: number;
  private _resendTimer: ReturnType<typeof setInterval> | null = null;

  private _googleCleanup: (() => void) | null = null;
  private _googleAttempt: GoogleSigninAttempt = { status: 'closed' };

  private _actions: SigninActions | null = null;
  declare statusMessage: string;
  declare private _titleOverride: string | null;
  declare private _descriptionOverride: string | null;
  declare private _dialog: HTMLDialogElement | null;
  declare private _googleBtn: HTMLAnchorElement | null;
  declare private _emailInput: HTMLInputElement | null;
  private _signinSource = 'unknown';
  declare private _signinDialogNext: string | null;
  declare private _onSuccessCallback: (() => void) | null;
  declare private _pollTimer: ReturnType<typeof setTimeout> | null;
  declare private _triggerEl: HTMLElement | null;

  createRenderRoot(): ShadowRoot | this {
    return this;
  }

  constructor() {
    super();
    this._datasetSave = null;
    this._resendSeconds = 0;
    this.statusMessage = '';
    this._titleOverride = null;
    this._descriptionOverride = null;
    this._onSuccessCallback = null;
    this._pollTimer = null;
  }

  set actions(actions: SigninActions) {
    this._actions = actions;
  }

  private get requiredActions(): SigninActions {
    return requireActions('SigninDialogElement', this._actions);
  }

  firstUpdated() {
    this._dialog = this.renderRoot.querySelector('dialog');
    this._googleBtn = this.renderRoot.querySelector('#google-signin-btn');
    this._emailInput = this.renderRoot.querySelector('#signin-email');
  }

  open({ source = 'unknown', next = null, saveAfterSignin = false, onSuccess = null, onCodeSuccess = null, title = null, description = null, datasetSave = null }: any = {}) {
    const googleAttempt = this._beginGoogleAttempt();
    this.requiredActions.setAccountMenuOpen(false);
    this._prepareEmailStep();
    this._datasetSave = datasetSave;
    this._titleOverride = title || null;
    this._descriptionOverride = description || null;
    this._onSuccessCallback = onSuccess || null;
    this._onCodeSuccessCallback = onCodeSuccess || null;
    this._signinSource = signinSource(source);
    this._signinDialogNext = next;
    this._saveAfterSignin = saveAfterSignin;
    const target = next || this._signinReturnPath();
    this._setGoogleSigninTarget(target, Boolean(onSuccess));
    this.requiredActions.track('signin_dialog_opened', { signin_source: this._signinSource, tracking_version: '1' });
    // SAFETY: The application template owns this selector and supplies the stated DOM element type.
    this._triggerEl = document.activeElement as HTMLElement | null;
    if (this._dialog) {
      if (isFunction(this._dialog.showModal)) {
        this._dialog.showModal();
      } else {
        this._dialog.setAttribute('open', '');
      }
    }
    const googleContainer = this.querySelector<HTMLElement>('#google-signin-personalized');
    this._googleCleanup?.();
    if (googleContainer) this._googleCleanup = mountGoogleSignIn(googleContainer, {
      fields: () => signupAnalyticsFields(false),
      started: () => { this.statusMessage = ''; startSignin(this.requiredActions.track, 'google', this._signinSource); this.requiredActions.track('google_signin_started'); },
      success: async () => {
        if (!this._refreshGoogleAttempt(googleAttempt)) return;
        this._stopPolling();
        const callback = this._onSuccessCallback;
        const next = this._signinDialogNext;
        await this.requiredActions.fetchCurrentUser({ force: true });
        if (!this._ownsGoogleAttempt(googleAttempt)) return;
        this._resetEmailStep();
        if (callback) { this._close(); callback(); }
        else {
          const target = next || await this._draftSigninTarget(this.requiredActions);
          if (!this._ownsGoogleAttempt(googleAttempt)) return;
          await this.requiredActions.navigateWithDraft(target);
          if (this._ownsGoogleAttempt(googleAttempt)) this._close();
        }
      },
      failed: () => {
        if (!this._ownsGoogleAttempt(googleAttempt)) return;
        this._googleCleanup?.();
        this._googleCleanup = null;
        this.statusMessage = t('signin_dialog.error_unavailable');
      },
    });
    this._focusSigninStep();
  }

  private _setGoogleSigninTarget(target: string, popup: boolean) {
    if (!this._googleBtn) return;
    this._googleBtn.href = popup
      ? `/api/auth/google?next=${encodeURIComponent('/')}&popup=1`
      : `/api/auth/google?next=${encodeURIComponent(target)}`;
  }

  private _prepareEmailStep() {
    if (Date.now() >= this._emailExpiresAt) this._resetEmailStep();
  }

  private _focusSigninStep() {
    if (this._sentEmail) {
      this._startPolling();
      void this.updateComplete.then(() => {
        if (this._dialog?.open) this.querySelector<HTMLInputElement>('#signin-code')?.focus();
      });
    } else this._focusEmailInput();
  }

  private _focusEmailInput() {
    const emailInput = this._emailInput;
    if (!this._datasetSave && emailInput && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      setTimeout(() => emailInput.focus(), 0);
    }
  }

  _close() {
    this._endGoogleAttempt();
    this._googleCleanup?.();
    this._googleCleanup = null;
    this._cancelEmailRequests();
    this._stopPolling();
    this._onSuccessCallback = null;
    this._onCodeSuccessCallback = null;
    if (this._dialog) {
      if (isFunction(this._dialog.close)) this._dialog.close();
      this._dialog.removeAttribute('open');
    }
    this._triggerEl?.focus();
    this._triggerEl = null;
  }

  private _finishEmailSignin(callback: (() => void) | null) {
    this._resetEmailStep();
    this._close();
    if (callback) callback();
    else if (this._saveAfterSignin && !this._signinDialogNext) this.requiredActions.showToast(t('signin_dialog.return_tab'), { duration: null });
  }

  private _pollGeneration = 0;

  private _endGoogleAttempt() {
    const attempt = this._googleAttempt;
    this._googleAttempt = { status: 'closed' };
    if (attempt.status === 'popup') {
      attempt.removeMessage();
      if (!attempt.popup.closed) attempt.popup.close();
    }
  }

  private _beginGoogleAttempt() {
    this._endGoogleAttempt();
    this._stopPolling();
    const token = Symbol('google-signin');
    this._googleAttempt = { status: 'ready', token };
    return token;
  }

  private _ownsGoogleAttempt(token: symbol) {
    return this._googleAttempt.status !== 'closed' && this._googleAttempt.token === token;
  }

  private _refreshGoogleAttempt(token: symbol) {
    const attempt = this._googleAttempt;
    if ((attempt.status !== 'ready' && attempt.status !== 'popup') || attempt.token !== token) return false;
    if (attempt.status === 'popup') {
      attempt.removeMessage();
      if (!attempt.popup.closed) attempt.popup.close();
    }
    this._googleAttempt = { status: 'refreshing', token };
    return true;
  }

  _stopPolling() {
    this._pollGeneration++;
    if (this._pollTimer) { clearTimeout(this._pollTimer); this._pollTimer = null; }
  }

  _startPolling(googleAttempt?: symbol) {
    this._stopPolling();
    const generation = this._pollGeneration;
    const current = () => generation === this._pollGeneration && (!googleAttempt || this._ownsGoogleAttempt(googleAttempt));
    const callback = this._onSuccessCallback;
    let attempts = 0;
    let failureReported = false;
    const maxAttempts = 150; // ~5 minutes at 2s interval
    const reportFailure = (props: Record<string, string>) => {
      if (failureReported) return;
      failureReported = true;
      // SAFETY: The registered event name and listener contract define this concrete event type.
      (this.requiredActions.track as (event: string, values?: Record<string, string>) => void)('auth_state_fetch_failed', props);
    };
    const poll = async () => {
      try {
        const res = await fetch('/api/auth/me', { credentials: 'same-origin' });
        if (!current()) return;
        if (!res.ok) {
          const error = new Error(`Sign-in status request failed with HTTP ${res.status}`);
          console.warn(error.message);
          reportFailure({
            reason: 'http_error',
            online_state: navigator.onLine ? 'online' : 'offline',
            http_status: String(res.status),
            content_type: res.headers.get('content-type')?.includes('json') ? 'json' : 'other',
            error_blob: JSON.stringify({ message: error.message }),
          });
        } else {
          const data: SchemaInput = await res.json();
          if (!current()) return;
          if (signedInEmail(data)) {
            if (googleAttempt && !this._refreshGoogleAttempt(googleAttempt)) return;
            await this.requiredActions.fetchCurrentUser({ force: true });
            if (!current()) return;
            this._finishEmailSignin(callback);
            return;
          }
          if (!isSchemaRecord(data) || !('user' in data)) {
            const error = new Error('Sign-in status response has an invalid shape');
            console.warn(error.message);
            reportFailure({
              reason: 'invalid_response',
              online_state: navigator.onLine ? 'online' : 'offline',
              content_type: 'json',
              error_blob: JSON.stringify({ message: error.message }),
            });
          }
        }
      } catch (error) {
        if (!current()) return;
        if (googleAttempt && this._googleAttempt.status === 'refreshing') this._googleAttempt = { status: 'ready', token: googleAttempt };
        console.warn('Sign-in status request failed:', error);
        reportFailure(this._pollFailureProps(error));
      }
      if (!current()) return;
      attempts++;
      if (attempts < maxAttempts) {
        this._pollTimer = setTimeout(poll, 2000);
      } else {
        this._pollTimer = null;
        this.statusMessage = t('signin_dialog.error_confirmation_timeout');
        this.requiredActions.track('signin_poll_expired', { attempt_count: String(attempts) });
        console.warn(`Sign-in status polling stopped after ${attempts} attempts`);
      }
    };
    this._pollTimer = setTimeout(poll, 2000);
  }

  private _pollFailureProps(cause: unknown) {
    return {
      reason: cause instanceof SyntaxError ? 'response_read_error' : 'network_error',
      online_state: navigator.onLine ? 'online' : 'offline',
      error_blob: JSON.stringify({ message: cause instanceof Error ? cause.message : String(cause) }),
    };
  }

  private _signinReturnPath() {
    if (this._saveAfterSignin) return '/?save_draft=1';
    const url = new URL(window.location.href);
    url.searchParams.delete('save_draft');
    return `${url.pathname}${url.search}`;
  }

  async _draftSigninTarget(actions: SigninActions) {
    const path = this._signinReturnPath();
    let target = `${path}${window.location.hash}`;
    try {
      const hash = await actions.compress(actions.encodeData());
      target = `${path}#${hash}`;
    } catch { /* retain the existing URL hash if encoding fails */ }
    return target;
  }

  _onBackdropClick(e: Event) {
    if (e.target === this._dialog) this._close();
  }

  async _handleGoogleSignIn(e: Event) {
    e.preventDefault();
    const attempt = this._beginGoogleAttempt();
    const actions = this.requiredActions;
    startSignin(actions.track, 'google', this._signinSource);
    actions.track('google_signin_started');

    if (this._onSuccessCallback) {
      // Popup mode: keep the user on the map, detect auth via postMessage
      const url = signupAnalyticsUrl(`/api/auth/google?next=${encodeURIComponent('/')}&popup=1`, false);
      const w = 500, h = 600;
      const left = Math.round(window.screenX + (window.outerWidth - w) / 2);
      const top = Math.round(window.screenY + (window.outerHeight - h) / 2);
      const popup = window.open(url, 'google_signin', `width=${w},height=${h},left=${left},top=${top},toolbar=0,menubar=0`);
      if (!popup || popup.closed) {
        // Popup blocked, so fall back to full-page redirect
        const target = await this._draftSigninTarget(actions);
        if (!this._ownsGoogleAttempt(attempt)) return;
        this._googleAttempt = { status: 'redirecting', token: attempt };
        await actions.navigateWithDraft(signupAnalyticsUrl(`/api/auth/google?next=${encodeURIComponent(target)}`, false));
        if (this._ownsGoogleAttempt(attempt)) this._close();
        return;
      }
      const callback = this._onSuccessCallback;
      const onMessage = async (ev: MessageEvent) => {
        if (ev.origin !== window.location.origin) return;
        if (ev.data?.type !== 'auth_success') return;
        if (!this._refreshGoogleAttempt(attempt)) return;
        this._stopPolling();
        try {
          await actions.fetchCurrentUser({ force: true });
        } catch (error) {
          if (!this._ownsGoogleAttempt(attempt)) return;
          console.warn('Sign-in account refresh failed:', error);
          this._googleAttempt = { status: 'ready', token: attempt };
          this._startPolling(attempt);
          return;
        }
        if (!this._ownsGoogleAttempt(attempt)) return;
        this._resetEmailStep();
        this._close();
        callback();
      };
      window.addEventListener('message', onMessage);
      this._googleAttempt = { status: 'popup', token: attempt, popup, removeMessage: () => window.removeEventListener('message', onMessage) };
      this._startPolling(attempt);
      return;
    }

    // Full-page redirect mode (existing behaviour)
    // Encode the current map state into the redirect hash so the auto-save
    // survives browsers (Safari, Firefox private mode) that wipe sessionStorage
    // during cross-origin OAuth redirects. sessionStorage is still saved as a
    // primary mechanism by navigateWithDraft; this is the fallback.
    const target = this._signinDialogNext || await this._draftSigninTarget(actions);
    if (!this._ownsGoogleAttempt(attempt)) return;
    this._googleAttempt = { status: 'redirecting', token: attempt };

    const googleUrl = signupAnalyticsUrl(`/api/auth/google?next=${encodeURIComponent(target)}`, false);
    await actions.navigateWithDraft(googleUrl);
    if (this._ownsGoogleAttempt(attempt)) this._close();
  }

  private _isCodeRequestCurrent(request: AbortController, generation: number) {
    return !request.signal.aborted && generation === this._pollGeneration;
  }

  private async _focusCodeAfterUpdate(request: AbortController, select: boolean, generation = this._pollGeneration) {
    await this.updateComplete;
    if (!this._isCodeRequestCurrent(request, generation)) return;
    const input = this.querySelector<HTMLInputElement>('#signin-code');
    if (!input || input.disabled) return;
    input.focus();
    if (select) input.select();
    else input.value = '';
  }

  private _codeErrorMessage(data: SchemaInput) {
    const error = isSchemaRecord(data) ? data.error : null;
    if (error === 'invalid_code') return t('signin_dialog.code_invalid');
    if (error === 'code_unavailable') return t('signin_dialog.code_unavailable');
    return t('signin_dialog.error_unavailable');
  }

  private async _handleCodeSubmit(event: Event) {
    event.preventDefault();
    const input = this.querySelector<HTMLInputElement>('#signin-code');
    if (!input?.reportValidity()) return;
    const code = input.value.trim();
    const request = this._emailSession.verify();
    if (!request) return;
    this.statusMessage = '';
    this._stopPolling();
    const generation = this._pollGeneration;
    const callback = this._onCodeSuccessCallback || this._onSuccessCallback;
    const next = this._signinDialogNext;
    let selectCode = false;
    try {
      const response = await fetch('/api/auth/verify-code', {
        method: 'POST', signal: request.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge: this._challenge, code }),
      });
      if (!this._isCodeRequestCurrent(request, generation)) return;
      const data: SchemaInput = await response.json();
      if (!this._isCodeRequestCurrent(request, generation)) return;
      if (!response.ok) {
        this.statusMessage = this._codeErrorMessage(data);
        selectCode = true;
        return;
      }
      await this.requiredActions.fetchCurrentUser({ force: true });
      if (!this._isCodeRequestCurrent(request, generation)) return;
      this._resetEmailStep();
      this._close();
      if (callback) callback();
      else if (next && next !== `${location.pathname}${location.search}${location.hash}`) {
        await this.requiredActions.navigateWithDraft(next);
      }
    } catch {
      if (!request.signal.aborted) this.statusMessage = t('signin_dialog.error_unavailable');
    } finally {
      if (this._emailSession.finish(request)) {
        if (selectCode) await this._focusCodeAfterUpdate(request, true, generation);
        if (this._sentEmail) this._startPolling();
      }
    }
  }

  private _cancelEmailRequests() {
    this._emailSession.cancel();
  }

  private _resetEmailStep() {
    this._emailSession.reset();
    if (this._resendTimer) clearInterval(this._resendTimer);
    this._resendTimer = null;
    this._resendSeconds = 0;
    this.statusMessage = '';
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._close();
    this._resetEmailStep();
  }

  private _startResendDelay() {
    if (this._resendTimer) clearInterval(this._resendTimer);
    const deadline = Date.now() + 60_000;
    this._resendSeconds = 60;
    this._resendTimer = setInterval(() => {
      this._resendSeconds = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      if (!this._resendSeconds && this._resendTimer) {
        clearInterval(this._resendTimer);
        this._resendTimer = null;
      }
    }, 1000);
  }

  private async _changeEmail() {
    this._stopPolling();
    this._resetEmailStep();
    await this.updateComplete;
    this._emailInput?.focus();
  }

  private async _handleMagicLinkFailure(res: Response, actions: SigninActions, request: AbortController) {
    const reason = magicLinkFailureReason(res.status, await res.json().catch(() => null));
    if (!this._emailSession.owns(request)) return;
    actions.track('magic_link_request_failed', { reason, http_status: String(res.status) });
    const messages = {
      invalid_email: 'signin_dialog.error_invalid_email',
      disposable_email: 'signin_dialog.error_disposable_email',
      invalid_context: 'signin_dialog.error_invalid_context',
      rate_limited: 'signin_dialog.error_rate_limited',
      delivery_failed: 'signin_dialog.error_send',
      service_unavailable: 'signin_dialog.error_unavailable',
      request_rejected: 'signin_dialog.error_rejected',
    };
    this.statusMessage = t(messages[reason]);
    if (reason === 'rate_limited' && this._sentEmail) this._startResendDelay();
  }

  async _handleMagicLinkSubmit(e: Event) {
    e.preventDefault();
    if (this._sentEmail && this._resendSeconds > 0) return;
    const actions = this.requiredActions;
    const email = this._sentEmail || this._emailInput?.value.trim();
    if (!email) { this.statusMessage = t('signin_dialog.error_no_email'); return; }
    const request = this._emailSession.send();
    if (!request) return;
    this.statusMessage = '';
    startSignin(actions.track, 'email', this._signinSource);
    let focusCode = false;
    try {
      const redirectTo = this._signinDialogNext || await this._draftSigninTarget(actions);
      const res = await fetch('/api/auth/magic-link', {
        method: 'POST',
        signal: request.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, email_code: true, redirect_to: redirectTo, purpose: this._saveAfterSignin ? 'save' : 'signin', variant: 'signin_dialog', ...signupAnalyticsFields(false) })
      });
      if (!this._emailSession.owns(request)) return;
      if (!res.ok) {
        await this._handleMagicLinkFailure(res, actions, request);
        return;
      }
      const delivery: SchemaInput = await res.json();
      if (!this._emailSession.owns(request)) return;
      const challenge = isSchemaRecord(delivery) && isString(delivery.challenge) ? delivery.challenge : '';
      this._emailSession.delivered(request, email, challenge, Date.now() + 15 * 60_000);
      this._startResendDelay();
      this._stopPolling();
      this._startPolling();
      actions.track('magic_link_sent');
      focusCode = true;
    } catch {
      if (!this._emailSession.owns(request)) return;
      actions.track('magic_link_request_failed', { reason: 'network_error' });
      this.statusMessage = t('signin_dialog.error_send');
    } finally {
      if (this._emailSession.finish(request)) {
        if (focusCode) await this._focusCodeAfterUpdate(request, false);
      }
    }
  }

  render(): TemplateResult {
    return html`
      <dialog data-dataset-save=${Boolean(this._datasetSave) && !this._sentEmail} id="signin-dialog" class="share-dialog signin-dialog dlg:[&[data-dataset-save=true]]:max-w-[740px] dlg:[&[data-dataset-save=true]]:w-[calc(100vw_-_32px)] dlg:[&[data-dataset-save=true]]:max-h-[calc(100dvh_-_32px)] dlg:[&[data-dataset-save=true]_.share-form]:p-[28px] dlg:[&[data-dataset-save=true]_h2]:text-[28px] dlg:[&[data-dataset-save=true]_h2]:leading-[1.15] dlg:[&[data-dataset-save=true]_h2]:tracking-[-0.03em] dlg:[&[data-dataset-save=true]_#magic-link-submit]:[background:#15803d] dlg:[&[data-dataset-save=true]_#magic-link-submit]:[color:#fff] dlg:[&[data-dataset-save=true]_.embed-description]:text-[13px] dlg:[&[data-dataset-save=true]_.embed-description]:leading-[1.5] dlg:[&[data-dataset-save=true]_.embed-description]:[color:var(--text-secondary)] dlg:[@media_(max-width:_640px)]:[&[data-dataset-save=true]_.share-form]:p-[20px] dlg:[@media_(max-width:_640px)]:[&[data-dataset-save=true]_h2]:text-[24px] dlg:[&.upgrade-dialog]:max-w-[500px] dlg:[&.upgrade-dialog]:max-h-[min(90vh,_720px)] dlg:supports-[height:100dvh]:[&.upgrade-dialog]:max-h-[min(90dvh,_720px)] dlg:[@media_(max-width:_768px)]:[&.upgrade-dialog]:max-h-[92vh] dlg:supports-[height:100dvh]:[@media_(max-width:_768px)]:[&.upgrade-dialog]:max-h-[92dvh] mchrome:w-max-768:w-[100%] mchrome:w-max-768:max-w-[100%] mchrome:w-max-768:inset-[auto_0_0] mchrome:w-max-768:[transform:none] mchrome:w-max-768:rounded-[16px_16px_0_0] mchrome:w-max-768:max-h-[85vh] mchrome:supports-[height:100dvh]:w-max-768:max-h-[85dvh] dlg:[border:none] dlg:rounded-[16px] dlg:p-[0] dlg:max-w-[420px] dlg:w-[calc(100%_-_32px)] dlg:fixed dlg:right-[auto] dlg:bottom-[auto] dlg:top-[50%] dlg:left-[50%] dlg:m-[0] dlg:max-h-[min(70vh,_520px)] dlg:supports-[height:100dvh]:max-h-[min(70dvh,_520px)] dlg:h-[auto] dlg:[background:var(--bg-toolbar)] dlg:[color:var(--text-primary)] dlg:[box-shadow:0_24px_80px_rgb(0_0_0_/_25%)] dlg:[transform:translate(-50%,_-50%)] dlg:[&::backdrop]:[background:rgb(0_0_0_/_50%)] dlg:[&::backdrop]:[backdrop-filter:blur(4px)] dlg:w-[min(440px,_calc(100vw_-_24px))] dlg:max-w-[440px] dlg:max-h-[calc(100vh_-_24px)] dlg:supports-[height:100dvh]:max-h-[calc(100dvh_-_24px)] dlg:[&_[hidden]]:hidden dlg:[&_.share-form]:p-[16px_24px_24px] dlg:[&_.share-form]:gap-[8px] dlg:[&_.feedback-dialog-header]:m-[0] dlg:[&_.embed-description]:m-[0_0_8px] dlg:[&_.feedback-input]:box-border dlg:[&_.feedback-input]:mt-[4px] dlg:[&_.feedback-input]:min-h-[44px]" aria-labelledby="signin-dialog-title" @click=${this._onBackdropClick} @cancel=${this._close}>
        <div class="share-form mchrome:w-max-768:p-[16px] dlg:p-[24px] dlg:flex dlg:flex-col dlg:gap-[10px] confirm:[confirm-dialog-element_&]:p-[24px] confirm:[confirm-dialog-element_&]:flex confirm:[confirm-dialog-element_&]:flex-col confirm:[confirm-dialog-element_&]:gap-[10px] confirm:w-max-640:[confirm-dialog-element_&]:p-[16px]">
          <div class="feedback-dialog-header dlg:flex dlg:justify-between dlg:items-center dlg:mb-[4px] dlg:[&_h2]:text-[18px] dlg:[&_h2]:font-[700] dlg:[&_h2]:[color:var(--text-primary)] dlg:[&_h2]:m-[0] confirm:[confirm-dialog-element_&]:flex confirm:[confirm-dialog-element_&]:justify-between confirm:[confirm-dialog-element_&]:items-center confirm:[confirm-dialog-element_&]:mb-[4px] confirm:[confirm-dialog-element_&_h2]:text-[18px] confirm:[confirm-dialog-element_&_h2]:font-[700] confirm:[confirm-dialog-element_&_h2]:[color:var(--text-primary,_#f1f5f9)] confirm:[confirm-dialog-element_&_h2]:m-[0]">
            <h2 id="signin-dialog-title" tabindex="-1">${this._sentEmail ? t('signin_dialog.enter_code') : this._titleOverride ?? t('signin_dialog.title')}</h2>
            <button class="feedback-dialog-close dlg:w-[32px] dlg:h-[32px] dlg:[border:none] dlg:[background:var(--bg-secondary)] dlg:rounded-[8px] dlg:[cursor:pointer] dlg:flex dlg:items-center dlg:justify-center dlg:[color:var(--text-muted)] dlg:[transition:all_0.15s_ease] dlg:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[background:var(--bg-hover)] dlg:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[color:var(--text-secondary)] dlg:[&:disabled]:[opacity:0.55] dlg:[&:disabled]:[cursor:default] confirm:[confirm-dialog-element_&]:w-[32px] confirm:[confirm-dialog-element_&]:h-[32px] confirm:[confirm-dialog-element_&]:[border:none] confirm:[confirm-dialog-element_&]:[background:var(--bg-secondary,_#334155)] confirm:[confirm-dialog-element_&]:rounded-[8px] confirm:[confirm-dialog-element_&]:[cursor:pointer] confirm:[confirm-dialog-element_&]:flex confirm:[confirm-dialog-element_&]:items-center confirm:[confirm-dialog-element_&]:justify-center confirm:[confirm-dialog-element_&]:[color:var(--text-muted,_#94a3b8)] confirm:[confirm-dialog-element_&]:[transition:all_0.15s_ease] confirm:[@media_(hover:_hover)_and_(pointer:_fine)]:[confirm-dialog-element_&:hover]:[background:var(--bg-hover,_#475569)] confirm:[@media_(hover:_hover)_and_(pointer:_fine)]:[confirm-dialog-element_&:hover]:[color:var(--text-secondary,_#cbd5e1)]" id="signin-close" title=${t('signin_dialog.close')} aria-label=${t('signin_dialog.close')} @click=${this._close}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <div ?hidden=${Boolean(this._sentEmail)} class=${this._datasetSave ? 'dataset-save-layout dlg:grid dlg:grid-cols-[1fr_1fr] dlg:gap-[28px] dlg:mt-[16px] dlg:[@media_(max-width:_640px)]:grid-cols-[1fr] dlg:[@media_(max-width:_640px)]:gap-[20px]' : ''}>
          ${this._datasetSave ? datasetSaveOffer(this._datasetSave) : null}
          <div>
          ${this._datasetSave ? html`<h3 class="dlg:m-[0_0_6px] dlg:text-[16px] dlg:font-[700]">${t('dataset_save.continue')}</h3>` : null}
          <p class="embed-description">${this._descriptionOverride ?? t('signin_dialog.description')}</p>
          <div class="google-signin-slot">
          <div id="google-signin-personalized" class="google-signin-button gs:w-[100%] gs:max-w-[400px] gs:[margin-inline:auto] gs:[&[data-ready]_+_a]:hidden"></div>
          <a id="google-signin-btn" class="feedback-btn feedback-btn-google gs:[a&]:[--google-button-bg:#fff] gs:[a&]:[--google-button-hover:#f8fafc] gs:[a&]:[--google-button-border:#747775] gs:[a&]:[--google-button-text:#1f1f1f] gs:[a&]:flex gs:[a&]:items-center gs:[a&]:justify-center gs:[a&]:gap-[10px] gs:[a&]:box-border gs:[a&]:w-[100%] gs:[a&]:min-h-[48px] gs:[a&]:p-[9px_14px] gs:[a&]:rounded-[8px] gs:[a&]:[background:var(--google-button-bg)] gs:[a&]:[color:var(--google-button-text)] gs:[a&]:[border:1px_solid_var(--google-button-border)] gs:[a&]:[cursor:pointer] gs:[a&]:[font-family:Roboto,_Arial,_sans-serif] gs:[a&]:text-[16px] gs:[a&]:font-[500] gs:[a&]:leading-[20px] gs:[a&]:[text-decoration:none] gs:[@media_(hover:_hover)_and_(pointer:_fine)]:[a&:hover]:[background:var(--google-button-hover)] gs:[a&:focus-visible]:[outline:2px_solid_#4285f4] gs:[a&:focus-visible]:[outline-offset:2px] gs:[[data-theme=dark]_a&]:[--google-button-bg:#131314] gs:[[data-theme=dark]_a&]:[--google-button-hover:#1f1f1f] gs:[[data-theme=dark]_a&]:[--google-button-border:#8e918f] gs:[[data-theme=dark]_a&]:[--google-button-text:#e3e3e3] dlg:[border:none] dlg:rounded-[8px] dlg:p-[9px_14px] dlg:text-[13px] dlg:font-[600] dlg:[cursor:pointer] dlg:[transition:all_0.15s_ease] dlg:[&:disabled]:[opacity:0.55] dlg:[&:disabled]:[cursor:default] dlg:[#magic-link-submit:where(&)]:mt-[12px] confirm:[confirm-dialog-element_&]:[border:none] confirm:[confirm-dialog-element_&]:rounded-[8px] confirm:[confirm-dialog-element_&]:p-[9px_14px] confirm:[confirm-dialog-element_&]:text-[13px] confirm:[confirm-dialog-element_&]:font-[600] confirm:[confirm-dialog-element_&]:[cursor:pointer] confirm:[confirm-dialog-element_&]:[transition:all_0.15s_ease]" href="/api/auth/google?next=%2F" @click=${this._handleGoogleSignIn}>
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            ${t('signin_dialog.continue_google')}
          </a>
          </div>
          <div class="signin-divider dlg:flex dlg:items-center dlg:gap-[12px] dlg:m-[10px_0_12px] dlg:[color:var(--text-secondary)] dlg:text-[13px] dlg:[&_span]:[flex:1] dlg:[&_span]:h-[1px] dlg:[&_span]:[background:var(--border-color)]"><span></span>${t('signin_dialog.or')}<span></span></div>
          <form id="magic-link-form" class="dlg:[&:has(#signin-email:valid)_#magic-link-submit:not(:disabled)]:[background:#15803d] dlg:[&:has(#signin-email:valid)_#magic-link-submit:not(:disabled)]:[color:#fff] dlg:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:has(#signin-email:valid)_#magic-link-submit:not(:disabled):hover]:[background:#166534]" @submit=${this._handleMagicLinkSubmit}>
            <label class="feedback-field-label dlg:text-[13px] dlg:font-[600] dlg:[color:var(--text-primary)]" for="signin-email">${t('signin_dialog.email_label')}</label>
            <input id="signin-email" class="feedback-input dlg:[#share-url:where(&)]:[font-family:Monaco,_Consolas,_monospace] dlg:[#share-url:where(&)]:text-[13px] dlg:w-[100%] dlg:[border:1px_solid_var(--border-color)] dlg:rounded-[10px] dlg:p-[10px_12px] dlg:[background:var(--bg-input)] dlg:[color:var(--text-primary)] dlg:text-[16px] dlg:[&:focus]:[outline:none] dlg:[&:focus]:[border-color:#3b82f6] dlg:[&:focus]:[box-shadow:0_0_0_3px_rgb(59_130_246_/_15%)]" type="email" placeholder=${t('signin_dialog.email_placeholder')} autocomplete="email" spellcheck="false" required>
            <button id="magic-link-submit" class="feedback-btn feedback-btn-secondary signin-wide dlg:[border:none] dlg:rounded-[8px] dlg:p-[9px_14px] dlg:text-[13px] dlg:font-[600] dlg:[cursor:pointer] dlg:[transition:all_0.15s_ease] dlg:[&:disabled]:[opacity:0.55] dlg:[&:disabled]:[cursor:default] dlg:[#magic-link-submit:where(&)]:mt-[12px] dlg:[background:var(--bg-secondary)] dlg:[color:var(--text-primary)] dlg:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[background:var(--bg-hover)] dlg:w-[100%] dlg:min-h-[44px] confirm:[confirm-dialog-element_&]:[border:none] confirm:[confirm-dialog-element_&]:rounded-[8px] confirm:[confirm-dialog-element_&]:p-[9px_14px] confirm:[confirm-dialog-element_&]:text-[13px] confirm:[confirm-dialog-element_&]:font-[600] confirm:[confirm-dialog-element_&]:[cursor:pointer] confirm:[confirm-dialog-element_&]:[transition:all_0.15s_ease] confirm:[confirm-dialog-element_&]:[background:var(--bg-secondary,_#334155)] confirm:[confirm-dialog-element_&]:[color:var(--text-secondary,_#cbd5e1)] confirm:[@media_(hover:_hover)_and_(pointer:_fine)]:[confirm-dialog-element_&:hover]:[background:var(--bg-hover,_#475569)]" type="submit" ?disabled=${this._sending}>${t(this._datasetSave ? 'dataset_save.email' : 'signin_dialog.send_code')}</button>
            <p class="signin-footer dlg:[color:var(--text-secondary)] dlg:leading-[1.5] dlg:m-[12px_0_0] dlg:text-center dlg:text-[12px]">${t(this._datasetSave ? 'dataset_save.existing' : 'signin_dialog.existing_account')}</p>
          </form>
          ${this._datasetSave ? html`<button class="dlg:block dlg:m-[12px_auto_0] dlg:min-h-[44px] dlg:p-[8px_16px] dlg:[border:0] dlg:[background:transparent] dlg:[color:var(--text-secondary)] dlg:text-[13px] dlg:[cursor:pointer] dlg:underline dlg:underline-offset-4" type="button" @click=${this._close}>${t('dataset_save.keep_editing')}</button>` : null}
          </div>
          </div>
          ${this._sentEmail ? html`
            <div class="signin-confirmation dlg:[color:var(--text-secondary)] dlg:text-[14px] dlg:leading-[1.5]">
              <p>${t('signin_dialog.code_sent_to')}<strong class="signin-recipient dlg:block dlg:mt-[6px] dlg:[color:var(--text-primary)] dlg:[overflow-wrap:anywhere]">${this._sentEmail}</strong></p>
              <p class="signin-expiry">${t('signin_dialog.code_expiry')}</p>
              <form class="signin-code-form" @submit=${this._handleCodeSubmit}>
                <label for="signin-code">${t('signin_dialog.code_label')}</label>
                <input id="signin-code" class="feedback-input" type="text" inputmode="numeric" autocomplete="one-time-code" pattern="[0-9]{6}" dir="ltr" required @input=${normalizeEmailCodeInput} ?disabled=${this._verifying || this._sending}>
                <button id="signin-code-submit" class="feedback-btn feedback-btn-primary" type="submit" ?disabled=${this._verifying || this._sending || !this._challenge}>${t(this._verifying ? 'signin_dialog.verifying_code' : 'signin_dialog.verify_code')}</button>
              </form>
              <p class="signin-link-option">${t('signin_dialog.code_link_option')}</p>

              <div class="signin-recovery">
                <button class="signin-change-email" type="button" @click=${this._changeEmail}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m16 3 5 5-12 12-6 1 1-6Z"/><path d="m14 5 5 5"/></svg>
                  ${t('signin_dialog.change_email')}
                </button>
                <button id="signin-resend" class="signin-resend" type="button" ?disabled=${this._sending || this._verifying || this._resendSeconds > 0} @click=${this._handleMagicLinkSubmit}>${this._resendSeconds > 0 ? t('signin_dialog.resend_code_delay').replace('%s', String(this._resendSeconds)) : t('signin_dialog.resend_code')}</button>
              </div>
              <p class="signin-spam-help">${t('signin_dialog.spam_help')}</p>
            </div>
          ` : null}
          <p id="signin-status" class="feedback-status dlg:m-[8px_0_0] dlg:p-[12px_14px] dlg:rounded-[8px] dlg:[border:1px_solid_#f87171] dlg:[background:#fef2f2] dlg:[color:#991b1b] dlg:text-[14px] dlg:font-[600] dlg:leading-[1.5]" role="alert" ?hidden=${!this.statusMessage}>${this.statusMessage}</p>
        </div>
      </dialog>
    `;
  }
}

customElements.define('signin-dialog-element', SigninDialogElement);
